import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Avatar, Box, Button, Chip, Divider, Grid, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import {
  MdArrowBack,
  MdPrint,
  MdReceiptLong,
  MdLocalShipping,
  MdPerson,
  MdPlace,
  MdPayments,
  MdInventory2,
  MdCheckCircle,
  MdContentCopy,
  MdStorefront,
  MdWarningAmber,
  MdHistory,
  MdRefresh,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  PAYMENT_MODE,
  PAYMENT_STATUS,
  PROGRESS_STEPS,
  deliveredAt,
  expectedDelivery,
  fmtDate,
  fmtDateTime,
  inr,
  isDelayed,
  itemName,
  moneyNum,
  paymentInfo,
  pill,
  progressStep,
  statusOf,
  timeAgo,
} from './orderShared';

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const label = (s) => (s ? String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');

const Card = ({ title, icon: Icon, accent = '#6366f1', children, right, sx }) => (
  <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, p: 2.5, ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {Icon && <Icon size={20} color={accent} />}
        <Typography sx={{ fontWeight: 800, fontSize: 15.5, fontFamily: FONT, color: '#0f172a' }}>{title}</Typography>
      </Box>
      {right}
    </Box>
    {children}
  </Paper>
);

const Row = ({ k, v, bold, color, mb = 1.1 }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb }}>
    <Typography sx={{ fontSize: 13.5, color: '#64748b', fontFamily: FONT }}>{k}</Typography>
    <Typography sx={{ fontSize: 13.5, fontWeight: bold ? 800 : 600, color: color || '#0f172a', fontFamily: FONT, textAlign: 'right', wordBreak: 'break-word' }}>{v ?? '—'}</Typography>
  </Box>
);

const OrderDetailsPage = ({ orderId }) => {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(`order/${orderId}`, null, authToken(), false);
      if (res?.order) setOrder(res.order);
      else setError(res?.message || 'Order not found');
    } catch (err) {
      setError(err.message || 'Could not load this order');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const back = () => navigate('/ordermanagement');

  if (loading && !order) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }
  if (error || !order) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<MdArrowBack />} onClick={back} sx={{ textTransform: 'none', fontWeight: 700, mb: 2 }}>Back to orders</Button>
        <Alert severity="error">{error || 'Order not found'}</Alert>
      </Box>
    );
  }

  const o = order;
  const st = statusOf(o.orderStatus);
  const pay = paymentInfo(o);
  const exp = expectedDelivery(o);
  const delivered = deliveredAt(o);
  const delayed = isDelayed(o);
  const addr = o.shippingAddress || {};
  const cust = o.customerId || {};
  const courier = o.courierDetails || {};
  const history = o.statusHistory || [];
  const sellerRequests = o.sellerRequests || [];
  const failed = ['cancelled', 'returned', 'failed'].includes(o.orderStatus);
  const step = progressStep(o.orderStatus);

  const STEP_STATUSES = [['placed', 'pending'], ['packed', 'ready-for-dispatch'], ['dispatched', 'in-transit'], ['out-for-delivery'], ['delivered']];
  const stepTime = (i) => {
    const h = history.find((x) => x.isActive && STEP_STATUSES[i].includes(x.status));
    return h?.changedAt || (i === 0 ? o.createdAt : null);
  };

  const subtotal = moneyNum(o.subtotal);
  const discount = moneyNum(o.discountTotal);
  const coupon = moneyNum(o.couponDiscount);
  const delivery = moneyNum(o.deliveryCharges);
  const cod = moneyNum(o.codCharge);
  const roundOff = moneyNum(o.roundOff);
  const wallet = moneyNum(o.walletUsedAmount);
  const points = moneyNum(o.redeemPointsAmount);
  const orderValue = moneyNum(o.orderValue) || Math.abs(moneyNum(o.totalAmount)) + wallet + points;
  const totalPieces = (o.items || []).reduce((a, i) => a + (i.quantity || 0), 0);

  const copyAwb = () => {
    navigator.clipboard?.writeText(courier.awb_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, background: '#f8fafc', minHeight: '100%' }}>
      <Button startIcon={<MdArrowBack />} onClick={back} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', mb: 1 }}>
        Back to orders
      </Button>

      {/* Header */}
      <Paper elevation={0} sx={{ borderRadius: '16px', p: 2.5, mb: 2.5, color: '#fff', background: `linear-gradient(135deg, #0f172a, ${st.fg})`, boxShadow: '0 8px 20px rgba(15,23,42,.15)' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 26, fontWeight: 800, fontFamily: FONT }}>Order {o.orderNumber}</Typography>
              <Chip label={st.label} sx={{ ...pill(st.bg, st.fg), height: 28, fontSize: 13 }} />
              <Chip label={`${pay.method} · ${pay.label}`} sx={{ ...pill(pay.bg, pay.fg), height: 28, fontSize: 13 }} />
              {delayed && <Chip icon={<MdWarningAmber />} label="Delayed" sx={{ ...pill('#fee2e2', '#b91c1c'), height: 28, '& .MuiChip-icon': { color: 'inherit' } }} />}
            </Box>
            <Typography sx={{ fontSize: 13.5, opacity: 0.85, mt: 0.75, fontFamily: FONT }}>
              Placed {fmtDateTime(o.createdAt)} ({timeAgo(o.createdAt)}){o.invoiceNumber ? ` · Invoice ${o.invoiceNumber}` : ''} · {totalPieces} item{totalPieces === 1 ? '' : 's'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 30, fontWeight: 800, fontFamily: FONT, mr: 1 }}>{inr(Math.abs(o.totalAmount))}</Typography>
            {o.customerInvoice && (
              <Button component="a" href={o.customerInvoice} target="_blank" rel="noopener noreferrer" startIcon={<MdReceiptLong />} sx={{ textTransform: 'none', fontWeight: 700, background: 'rgba(255,255,255,.18)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,.3)' } }}>
                Invoice
              </Button>
            )}
            <Button onClick={() => window.print()} startIcon={<MdPrint />} sx={{ textTransform: 'none', fontWeight: 700, background: 'rgba(255,255,255,.18)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,.3)' } }}>
              Print
            </Button>
            <IconButton onClick={load} sx={{ background: 'rgba(255,255,255,.18)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,.3)' } }}>
              <MdRefresh size={20} />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Progress tracker, or why the order stopped */}
      {failed ? (
        <Alert severity={o.orderStatus === 'returned' ? 'warning' : 'error'} sx={{ mb: 2.5, borderRadius: '12px', fontFamily: FONT }}>
          <b>This order was {o.orderStatus}</b>
          {o.cancelledAt ? ` on ${fmtDateTime(o.cancelledAt)}` : ''}
          {o.cancelledBy ? ` by ${label(o.cancelledBy)}` : ''}
          {o.cancelReason ? ` — ${o.cancelReason}` : ''}
          {o.refundStatus ? ` · Refund: ${label(o.refundStatus)}${o.refundMode ? ` (${label(o.refundMode)})` : ''}` : ''}
        </Alert>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', p: 2.5, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            {PROGRESS_STEPS.map((s, i) => {
              const done = i < step;
              const current = i === step - 1;
              const t = done ? stepTime(i) : null;
              return (
                <Box key={s.key} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', textAlign: 'center' }}>
                  {i > 0 && <Box sx={{ position: 'absolute', top: 17, right: '50%', width: '100%', height: 4, background: done ? '#10b981' : '#e2e8f0', zIndex: 0 }} />}
                  <Box
                    sx={{
                      zIndex: 1,
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: done ? '#10b981' : '#fff',
                      border: `3px solid ${done ? '#10b981' : '#e2e8f0'}`,
                      color: done ? '#fff' : '#94a3b8',
                      fontWeight: 800,
                      fontSize: 14,
                      boxShadow: current ? '0 0 0 5px rgba(16,185,129,.2)' : 'none',
                    }}
                  >
                    {done ? <MdCheckCircle size={20} /> : i + 1}
                  </Box>
                  <Typography sx={{ mt: 0.75, fontSize: 13, fontWeight: done ? 800 : 600, color: done ? '#0f172a' : '#94a3b8', fontFamily: FONT }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT, minHeight: 16 }}>{t ? fmtDateTime(t) : i === 4 && !done && exp ? `Expected ${fmtDate(exp)}` : ''}</Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <Grid container spacing={2.5}>
        {/* Left: items, price, seller requests */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Card title={`Items (${totalPieces})`} icon={MdInventory2} accent="#6366f1">
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, '& th': { textAlign: 'left', fontSize: 11.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1, borderBottom: '1px solid #e2e8f0' }, '& td': { py: 1.5, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', fontSize: 13.5 }, '& tr:last-child td': { borderBottom: 'none' } }}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th style={{ textAlign: 'center' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Price</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Fulfilled by</th>
                  </tr>
                </thead>
                <tbody>
                  {(o.items || []).map((it, idx) => {
                    const qty = Number(it.quantity) || 1;
                    const total = it.isFree ? 0 : Number(it.total) || Number(it.price) * qty || 0;
                    const sellerStatus = it.status || 'pending';
                    const sc = sellerStatus === 'accepted' ? ['#dcfce7', '#166534'] : sellerStatus === 'rejected' ? ['#fee2e2', '#991b1b'] : ['#fef3c7', '#92400e'];
                    return (
                      <tr key={idx}>
                        <td>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: FONT }}>{itemName(it)}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                            {it.product_id?.upc && <Typography sx={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>UPC {it.product_id.upc}</Typography>}
                            {it.isFree && <Chip size="small" label="FREE GIFT" sx={pill('#dcfce7', '#166534', { height: 20, fontSize: 11 })} />}
                            {it.isCombo && <Chip size="small" label="COMBO" sx={pill('#ede9fe', '#5b21b6', { height: 20, fontSize: 11 })} />}
                            {it.product_id?.return_policy > 0 && <Typography sx={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>· {it.product_id.return_policy}-day return</Typography>}
                          </Box>
                          {it.rejectionReason && <Typography sx={{ fontSize: 12, color: '#b91c1c', mt: 0.5, fontFamily: FONT }}>Rejected: {it.rejectionReason}</Typography>}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700 }}>{qty}</td>
                        <td style={{ textAlign: 'right' }}>{it.isFree ? 'FREE' : inr(qty ? total / qty : it.price)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800 }}>{it.isFree ? '—' : inr(total)}</td>
                        <td style={{ textAlign: 'right' }}>
                          {it.seller_id ? <Chip size="small" label={`Seller · ${label(sellerStatus)}`} sx={pill(sc[0], sc[1], { height: 22, fontSize: 11.5 })} /> : <Chip size="small" label="Warehouse" sx={pill('#e0e7ff', '#3730a3', { height: 22, fontSize: 11.5 })} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Box>
            </Card>

            <Card title="Price breakdown" icon={MdReceiptLong} accent="#10b981">
              <Row k="Items subtotal (MRP)" v={inr(subtotal)} />
              {discount > 0 && <Row k="Product discount" v={`− ${inr(discount)}`} color="#166534" />}
              {coupon > 0 && <Row k={`Coupon${o.couponCode ? ` (${o.couponCode})` : ''}`} v={`− ${inr(coupon)}`} color="#166534" />}
              <Row k="Delivery charges" v={delivery === 0 ? 'FREE' : inr(delivery)} color={delivery === 0 ? '#166534' : undefined} />
              {cod > 0 && <Row k="COD charges" v={inr(cod)} />}
              {o.roundOff !== undefined && o.roundOff !== null && roundOff !== 0 && <Row k="Round off" v={inr(roundOff)} />}
              <Divider sx={{ my: 1.5 }} />
              <Row k="Order value" v={inr(orderValue)} bold />
              {points > 0 && <Row k={`Paid with reward points${o.redeemPoints ? ` (${o.redeemPoints} pts)` : ''}`} v={`− ${inr(points)}`} color="#166534" />}
              {wallet > 0 && <Row k="Paid from wallet" v={`− ${inr(wallet)}`} color="#166534" />}
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 16, fontFamily: FONT }}>{pay.isCod && pay.status !== 'paid' ? 'To collect on delivery' : 'Amount paid / payable'}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 20, fontFamily: FONT }}>{inr(Math.abs(o.totalAmount))}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.75, fontFamily: FONT }}>Prices include GST of {inr(moneyNum(o.gsttotal))}.</Typography>
            </Card>

            {sellerRequests.length > 0 && (
              <Card title={`Seller requests (${sellerRequests.length})`} icon={MdStorefront} accent="#f59e0b">
                {sellerRequests.map((r, i) => {
                  const rs = r.status === 'delivered' || r.status === 'accepted' ? ['#dcfce7', '#166534'] : r.status === 'rejected' ? ['#fee2e2', '#991b1b'] : ['#fef3c7', '#92400e'];
                  const last = [...(r.statusHistory || [])].filter((h) => h.isActive).pop();
                  return (
                    <Box key={r._id || i} sx={{ p: 1.5, mb: i < sellerRequests.length - 1 ? 1.5 : 0, borderRadius: '10px', background: '#f8fafc', display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Avatar sx={{ bgcolor: '#fef3c7', color: '#92400e', width: 40, height: 40 }}><MdStorefront /></Avatar>
                      <Box sx={{ flex: 1, minWidth: 180 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: FONT }}>{r.sellerId?.businessName || r.sellerId?.name || 'Seller'}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>
                          {[r.sellerId?.businessCity, r.sellerId?.businessState].filter(Boolean).join(', ')}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1.3, minWidth: 180 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 13.5, fontFamily: FONT }}>{r.product_id?.title || 'Product'} × {r.quantity}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>
                          {r.receiptId ? `${r.receiptId} · ` : ''}{last ? `${label(last.status)} ${fmtDate(last.changedAt)}` : r.estdArrivalDate ? `Arriving ${fmtDate(r.estdArrivalDate)}` : ''}
                        </Typography>
                        {r.rejectionReason && <Typography sx={{ fontSize: 12.5, color: '#b91c1c', fontFamily: FONT }}>Rejected: {r.rejectionReason}</Typography>}
                      </Box>
                      <Chip size="small" label={label(r.status)} sx={pill(rs[0], rs[1])} />
                    </Box>
                  );
                })}
              </Card>
            )}
          </Box>
        </Grid>

        {/* Right: customer, address, payment, shipping, history */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Card title="Customer" icon={MdPerson} accent="#ec4899">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Avatar src={cust.image || undefined} sx={{ width: 46, height: 46, bgcolor: '#fce7f3', color: '#9d174d', fontWeight: 800 }}>{(cust.name || addr.name || '?').charAt(0).toUpperCase()}</Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 15, fontFamily: FONT }}>{cust.name || addr.name || 'Guest'}</Typography>
                  {cust.customerType && <Chip size="small" label={cust.customerType === 'business' ? 'B2B' : 'B2C'} sx={pill(cust.customerType === 'business' ? '#ede9fe' : '#e0f2fe', cust.customerType === 'business' ? '#5b21b6' : '#075985', { height: 20, fontSize: 11 })} />}
                </Box>
              </Box>
              <Row k="Phone" v={addr.mobile || cust.mobile} />
              <Row k="Email" v={cust.email} mb={0} />
            </Card>

            <Card title="Delivery address" icon={MdPlace} accent="#0ea5e9">
              <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: FONT }}>{addr.name}</Typography>
              <Typography sx={{ fontSize: 13.5, color: '#334155', lineHeight: 1.6, fontFamily: FONT }}>
                {[addr.addressLine1, addr.addressLine2, addr.landmark].filter(Boolean).join(', ')}
                <br />
                {[addr.city, addr.state].filter(Boolean).join(', ')} {addr.pincode ? `– ${addr.pincode}` : ''}
                <br />
                {addr.country}
              </Typography>
              {addr.mobile && <Typography sx={{ fontSize: 13.5, color: '#64748b', mt: 0.5, fontFamily: FONT }}>📞 {addr.mobile}</Typography>}
            </Card>

            <Card title="Payment" icon={MdPayments} accent="#10b981" right={<Chip size="small" label={pay.label} sx={pill(pay.bg, pay.fg)} />}>
              {pay.lines.length === 0 ? (
                <Typography sx={{ fontSize: 13.5, color: '#64748b', fontFamily: FONT }}>{pay.isCod ? 'Cash on delivery — amount to be collected by the courier.' : 'No payment recorded yet.'}</Typography>
              ) : (
                pay.lines.map((l, i) => {
                  const ls = PAYMENT_STATUS[l.status] || { label: l.status, bg: '#f1f5f9', fg: '#475569' };
                  return (
                    <Box key={i} sx={{ pb: 1.25, mb: 1.25, borderBottom: i < pay.lines.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: FONT }}>{PAYMENT_MODE[l.mode] || l.mode || 'Payment'} · {inr(l.amount)}</Typography>
                        <Chip size="small" label={ls.label} sx={pill(ls.bg, ls.fg, { height: 20, fontSize: 11 })} />
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>
                        {[l.provider, l.paidAt ? fmtDateTime(l.paidAt) : null].filter(Boolean).join(' · ')}
                      </Typography>
                      {l.paymentId && <Typography sx={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{l.paymentId}</Typography>}
                    </Box>
                  );
                })
              )}
            </Card>

            <Card title="Shipping" icon={MdLocalShipping} accent="#8b5cf6">
              <Row k="Courier" v={courier.courier_name} />
              <Row
                k="AWB number"
                v={
                  courier.awb_code ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <a href={`https://shiprocket.co/tracking/${courier.awb_code}`} target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5', fontWeight: 700 }}>{courier.awb_code}</a>
                      <Tooltip title={copied ? 'Copied!' : 'Copy'} arrow>
                        <IconButton size="small" onClick={copyAwb}><MdContentCopy size={14} /></IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    'Not generated yet'
                  )
                }
              />
              {courier.shipment_cost && <Row k="Shipping cost" v={inr(courier.shipment_cost)} />}
              <Row k="Warehouse" v={o.warehouseID?.name} />
              <Row k={delivered ? 'Delivered on' : 'Expected delivery'} v={delivered ? fmtDateTime(delivered) : exp ? fmtDate(exp) : '—'} bold color={delayed ? '#b91c1c' : undefined} mb={0} />
            </Card>

            <Card title="Status history" icon={MdHistory} accent="#64748b">
              {history.length === 0 ? (
                <Typography sx={{ fontSize: 13.5, color: '#64748b', fontFamily: FONT }}>No updates recorded.</Typography>
              ) : (
                history.map((h, i) => {
                  const hs = statusOf(h.status);
                  return (
                    <Box key={h._id || i} sx={{ display: 'flex', gap: 1.5, pb: 1.5, opacity: h.isActive ? 1 : 0.45 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: h.isActive ? hs.fg : '#cbd5e1', mt: 0.5 }} />
                        {i < history.length - 1 && <Box sx={{ flex: 1, width: 2, background: '#e2e8f0', mt: 0.5 }} />}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5, fontFamily: FONT }}>{hs.label}</Typography>
                        {h.isActive && h.changedAt && <Typography sx={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>{fmtDateTime(h.changedAt)}</Typography>}
                        {h.note && <Typography sx={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>{typeof h.note === 'object' ? JSON.stringify(h.note) : h.note}</Typography>}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetailsPage;
